import { css } from 'aphrodite';
import * as React from 'react';
import type { IFormField } from '../../../../../models/Api';
import type { SectionedSurveyForm, SurveySection } from '../../types';
import { buildSectionGroups, collectSurveyFormFieldIds } from './buildSectionGroups';
import { styleSheet } from './styles';

interface Props {
	/** The V2 survey form whose surveyParts may include SurveySection containers. */
	surveyForm: SectionedSurveyForm | undefined;
	/** The flat customForm.fields array that carries render data for each question. */
	fields: IFormField<string>[];
	/**
	 * Set of field IDs that passed conditional-logic evaluation.
	 * Pass `undefined` to mean "show all" (V1 / no logic defined).
	 */
	visibleFieldIds: Set<string> | undefined;
	/**
	 * Render-prop supplied by the caller (response form or preview).
	 * Receives a resolved, visible IFormField and returns the field's UI node.
	 */
	renderField: (field: IFormField<string>) => React.ReactNode;
	/**
	 * Optional override for the section header element.
	 * If omitted, `DefaultSectionHeader` is used (title + description).
	 */
	renderSectionHeader?: (section: SurveySection, index: number) => React.ReactNode;
}

/**
 * Placeholder section header — renders the section title and optional description.
 * Final design polish is a later task; this gives development a readable default.
 */
const DefaultSectionHeader = ({ section }: { section: SurveySection }) => (
	<header className={css(styleSheet.sectionHeader)}>
		{section.title ? <h3 className={css(styleSheet.sectionTitle)}>{section.title}</h3> : null}
		{section.description ? <p className={css(styleSheet.sectionDescription)}>{section.description}</p> : null}
	</header>
);

/**
 * Shared render component for V2 custom-survey sections.
 *
 * - **V2 (sectioned) path**: groups visible fields by their owning `SurveySection`
 *   in document order, suppresses empty/hidden sections, and renders a section
 *   header above each group. A `renderSectionHeader` render-prop lets the caller
 *   (response form vs. preview) supply a custom header.
 * - **V1 fallback**: when `surveyForm` has no `SurveySection` parts (old flat
 *   surveys), renders the visible flat list exactly as before — no sections or
 *   headers added.
 *
 * Per-field rendering is entirely delegated to the `renderField` render-prop,
 * so this component stays decoupled from form submission, preview-mode state,
 * and field-type details.
 */
export const SurveySections = ({ surveyForm, fields, visibleFieldIds, renderField, renderSectionHeader }: Props) => {
	const groups = buildSectionGroups(surveyForm, fields, visibleFieldIds);

	// V1 fallback: no section parts — render the visible flat list as before.
	if (groups === null) {
		const referencedFieldIds = collectSurveyFormFieldIds(surveyForm);
		const visible = fields.filter(
			f => !f.isHidden && (visibleFieldIds === undefined || visibleFieldIds.has(f.id) || !referencedFieldIds.has(f.id))
		);
		return <>{visible.map(renderField)}</>;
	}

	return (
		<>
			{groups.map((group, i) =>
				group.section ? (
					<section key={group.section.id} className={css(styleSheet.section)}>
						{renderSectionHeader ? (
							renderSectionHeader(group.section, i)
						) : (
							<DefaultSectionHeader section={group.section} />
						)}
						{group.fields.map(renderField)}
					</section>
				) : (
					// Ungrouped flat questions have no section wrapper or header.
					// Content-based key avoids remounting when a preceding section becomes fully hidden.
					<React.Fragment key={group.fields[0]?.id ?? `flat-${i}`}>{group.fields.map(renderField)}</React.Fragment>
				)
			)}
		</>
	);
};
